import React from 'react';

import { blockTypeMessages as messages } from '@atlaskit/editor-common/messages';
import {
	BLOCK_QUOTE_MENU_ITEM,
	HEADING_1_MENU_ITEM,
	HEADING_2_MENU_ITEM,
	HEADING_3_MENU_ITEM,
	HEADING_4_MENU_ITEM,
	HEADING_5_MENU_ITEM,
	HEADING_6_MENU_ITEM,
	NORMAL_TEXT_MENU_ITEM,
	SMALL_TEXT_MENU_ITEM,
	TEXT_STYLES_MENU_SECTION_RANK,
} from '@atlaskit/editor-common/toolbar';
import {
	TextIcon as EditorToolbarTextIcon,
	QuoteIcon,
	HeadingOneIcon,
	HeadingTwoIcon,
	HeadingThreeIcon,
	HeadingFourIcon,
	HeadingFiveIcon,
	HeadingSixIcon,
	TextSmallIcon,
} from '@atlaskit/editor-toolbar';
import TextHeadingFiveIcon from '@atlaskit/icon-lab/core/text-heading-five';
import TextHeadingFourIcon from '@atlaskit/icon-lab/core/text-heading-four';
import TextHeadingOneIcon from '@atlaskit/icon-lab/core/text-heading-one';
import TextHeadingSixIcon from '@atlaskit/icon-lab/core/text-heading-six';
import TextHeadingThreeIcon from '@atlaskit/icon-lab/core/text-heading-three';
import TextHeadingTwoIcon from '@atlaskit/icon-lab/core/text-heading-two';
import TextIcon from '@atlaskit/icon/core/text';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type { BlockType, BlockTypeWithRank } from './types';
import { Text, H1, H2, H3, H4, H5, H6 } from './ui/ToolbarBlockType/icons';

export const NORMAL_TEXT: BlockType = {
	name: 'normal',
	title: messages.normal,
	nodeName: 'paragraph',
	tagName: 'p',
	icon: <TextIcon label="" />,
	LEGACY_icon: <Text />,
};
export const SMALL_TEXT: BlockType = {
	name: 'smallText',
	title: messages.smallText,
	nodeName: 'paragraph',
	markName: 'fontSize',
	icon: <TextSmallIcon label="" />,
	LEGACY_icon: <Text />,
};
export const HEADING_1: BlockType = {
	name: 'heading1',
	title: messages.heading1,
	nodeName: 'heading',
	tagName: 'h1',
	level: 1,
	icon: <TextHeadingOneIcon label="" />,
	LEGACY_icon: <H1 />,
};
export const HEADING_2: BlockType = {
	name: 'heading2',
	title: messages.heading2,
	nodeName: 'heading',
	tagName: 'h2',
	level: 2,
	icon: <TextHeadingTwoIcon label="" />,
	LEGACY_icon: <H2 />,
};
export const HEADING_3: BlockType = {
	name: 'heading3',
	title: messages.heading3,
	nodeName: 'heading',
	tagName: 'h3',
	level: 3,
	icon: <TextHeadingThreeIcon label="" />,
	LEGACY_icon: <H3 />,
};
export const HEADING_4: BlockType = {
	name: 'heading4',
	title: messages.heading4,
	nodeName: 'heading',
	tagName: 'h4',
	level: 4,
	icon: <TextHeadingFourIcon label="" />,
	LEGACY_icon: <H4 />,
};
export const HEADING_5: BlockType = {
	name: 'heading5',
	title: messages.heading5,
	nodeName: 'heading',
	tagName: 'h5',
	level: 5,
	icon: <TextHeadingFiveIcon label="" />,
	LEGACY_icon: <H5 />,
};
export const HEADING_6: BlockType = {
	name: 'heading6',
	title: messages.heading6,
	nodeName: 'heading',
	tagName: 'h6',
	level: 6,
	icon: <TextHeadingSixIcon label="" />,
	LEGACY_icon: <H6 />,
};
export const BLOCK_QUOTE: BlockType = {
	name: 'blockquote',
	title: messages.blockquote,
	nodeName: 'blockquote',
	tagName: 'blockquote',
};
export const CODE_BLOCK: BlockType = {
	name: 'codeblock',
	title: messages.codeblock,
	nodeName: 'codeBlock',
};
export const PANEL: BlockType = {
	name: 'panel',
	title: messages.infoPanel,
	nodeName: 'panel',
};
export const OTHER: BlockType = {
	name: 'other',
	title: messages.other,
	nodeName: '',
};

export const TEXT_BLOCK_TYPES: BlockType[] = [
	NORMAL_TEXT,
	SMALL_TEXT,
	HEADING_1,
	HEADING_2,
	HEADING_3,
	HEADING_4,
	HEADING_5,
	HEADING_6,
];

enum ToolbarBlockTypes {
	normal = 'normal',
	heading1 = 'heading1',
	heading2 = 'heading2',
	heading3 = 'heading3',
	heading4 = 'heading4',
	heading5 = 'heading5',
	heading6 = 'heading6',
	blockquote = 'blockquote',
}

export const toolbarBlockTypesWithRank = ({
	allowFontSize,
}: {
	allowFontSize?: boolean;
}): Record<ToolbarBlockTypes, BlockTypeWithRank> => ({
	normal: {
		...NORMAL_TEXT,
		icon: <EditorToolbarTextIcon size="small" label="" />,
		toolbarRank: TEXT_STYLES_MENU_SECTION_RANK[NORMAL_TEXT_MENU_ITEM.key],
		toolbarKey: NORMAL_TEXT_MENU_ITEM.key,
	},
	...(allowFontSize &&
		expValEquals('platform_editor_small_font_size', 'isEnabled', true) && {
			smallText: {
				...SMALL_TEXT,
				icon: <TextSmallIcon size="small" label="" />,
				toolbarRank: TEXT_STYLES_MENU_SECTION_RANK[SMALL_TEXT_MENU_ITEM.key],
				toolbarKey: SMALL_TEXT_MENU_ITEM.key,
			},
		}),
	heading1: {
		...HEADING_1,
		icon: <HeadingOneIcon size="small" label="" />,
		toolbarRank: TEXT_STYLES_MENU_SECTION_RANK[HEADING_1_MENU_ITEM.key],
		toolbarKey: HEADING_1_MENU_ITEM.key,
	},
	heading2: {
		...HEADING_2,
		icon: <HeadingTwoIcon size="small" label="" />,
		toolbarRank: TEXT_STYLES_MENU_SECTION_RANK[HEADING_2_MENU_ITEM.key],
		toolbarKey: HEADING_2_MENU_ITEM.key,
	},
	heading3: {
		...HEADING_3,
		icon: <HeadingThreeIcon size="small" label="" />,
		toolbarRank: TEXT_STYLES_MENU_SECTION_RANK[HEADING_3_MENU_ITEM.key],
		toolbarKey: HEADING_3_MENU_ITEM.key,
	},
	heading4: {
		...HEADING_4,
		icon: <HeadingFourIcon size="small" label="" />,
		toolbarRank: TEXT_STYLES_MENU_SECTION_RANK[HEADING_4_MENU_ITEM.key],
		toolbarKey: HEADING_4_MENU_ITEM.key,
	},
	heading5: {
		...HEADING_5,
		icon: <HeadingFiveIcon size="small" label="" />,
		toolbarRank: TEXT_STYLES_MENU_SECTION_RANK[HEADING_5_MENU_ITEM.key],
		toolbarKey: HEADING_5_MENU_ITEM.key,
	},
	heading6: {
		...HEADING_6,
		icon: <HeadingSixIcon size="small" label="" />,
		toolbarRank: TEXT_STYLES_MENU_SECTION_RANK[HEADING_6_MENU_ITEM.key],
		toolbarKey: HEADING_6_MENU_ITEM.key,
	},
	blockquote: {
		...BLOCK_QUOTE,
		icon: <QuoteIcon size="small" label="" />,
		toolbarRank: TEXT_STYLES_MENU_SECTION_RANK[BLOCK_QUOTE_MENU_ITEM.key],
		toolbarKey: BLOCK_QUOTE_MENU_ITEM.key,
	},
});

export type TextBlockTypes =
	| 'normal'
	| 'smallText'
	| 'heading1'
	| 'heading2'
	| 'heading3'
	| 'heading4'
	| 'heading5'
	| 'heading6';

export const FORMATTING_NODE_TYPES: string[] = ['heading', 'blockquote'];
export const FORMATTING_MARK_TYPES: string[] = [
	'em',
	'code',
	'strike',
	'strong',
	'underline',
	'textColor',
	'subsup',
	'backgroundColor',
];

export const WRAPPER_BLOCK_TYPES: BlockType[] = [BLOCK_QUOTE, CODE_BLOCK, PANEL];
export const ALL_BLOCK_TYPES: BlockType[] = TEXT_BLOCK_TYPES.concat(WRAPPER_BLOCK_TYPES);

export const getBlockTypesInDropdown = (
	includeBlockQuoteAsTextstyleOption?: boolean,
): BlockType[] => {
	return includeBlockQuoteAsTextstyleOption ? [...TEXT_BLOCK_TYPES, BLOCK_QUOTE] : TEXT_BLOCK_TYPES;
};

export const HEADINGS_BY_LEVEL: Record<number, BlockType> = TEXT_BLOCK_TYPES.reduce<
	Record<number, BlockType>
>((acc, blockType) => {
	if (blockType.level && blockType.nodeName === 'heading') {
		acc[blockType.level] = blockType;
	}

	return acc;
}, {});

export const HEADINGS_BY_NAME: {
	[blockType: string]: BlockType;
} = TEXT_BLOCK_TYPES.reduce(
	(acc, blockType) => {
		if (blockType.level && blockType.nodeName === 'heading') {
			acc[blockType.name] = blockType;
		}

		return acc;
	},
	{} as { [blockType: string]: BlockType },
);
